//------------------------------------------------------------------------
// Horizontal Bar Chart
//------------------------------------------------------------------------
// NOTE: Only import the necessary modules
// https://www.chartjs.org/docs/latest/getting-started/integration.html
// NOTE: Treeshaking doesn’t seem to be working when importing everything
// https://github.com/chartjs/Chart.js/issues/10163
// e.g. import Chart from 'chart.js/auto';
/* eslint no-unused-vars: "off" */
import {Chart, BarElement, BarController, Tooltip, LinearScale, CategoryScale} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import debounce from "lodash/debounce";
import { axis_font_opts, cursorPositioner } from '../helpers/chart-js-helpers';

Chart.register(BarElement, BarController, Tooltip, LinearScale, CategoryScale);

/**
 * Custom positioner - Allows for the tooltip to follow the cursor's pointer
 *
 * Notes:
 * - https://www.chartjs.org/docs/latest/configuration/tooltip.html#custom-position-modes
 * - https://codepen.io/AZIIZoom/pen/eYzJNZW?editors=1010
 * @function Tooltip.positioners.myCustomPositioner
 * @param elements {Chart.Element[]} the tooltip elements
 * @param eventPosition {Point} the position of the event in canvas coordinates
 * @returns {TooltipPosition} the tooltip position
 */
Tooltip.positioners.cursor = cursorPositioner;

class HorizontalBarChart {
  constructor(config) {
    this.el = config.el;
    this.canvas = this.el.querySelector('canvas');

    if (!this.canvas) {
      console.error(`Unable to initialize vertical bar chart, no matching element for ${this.el}.`);
      return false;
    }

    this.setData();
    this.setChartConfig();
    this.init();
  }

  init() {
    this.chart = new Chart(this.canvas, {
      type: 'bar',
      data: this.data,
      plugins: this.plugins,
      options: this.chartOpts
    });

    this.el.addEventListener('mouseout-chart', debounce(this.mouseoutChartHandler.bind(this), 100));
    this.el.addEventListener('mouseover-chart', debounce(this.mouseoverChartHandler.bind(this), 100));
  }

  mouseoutChartHandler(evt) {
    // Revert chart background color to its original form
    evt.detail.tooltip.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
      let colorLength = color.length;
      // Remove the original opacity from the color
      let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

      colors[index] = baseColor;
    });

    evt.detail.tooltip.chart.update();
  }

  mouseoverChartHandler(evt) {
    const { tooltip } = evt.detail;
    const selectedIndex = tooltip.dataPoints[0].dataIndex;
    // const datasetIndex = 0; // this is always 0 because we only have one dataset

    // Update Chart colors on hover
    evt.detail.chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
      let colorLength = color.length;
      // Remove the original opacity from the color
      let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

      if (selectedIndex === index) {
        colors[index] = baseColor;
      } else {
        colors[index] = baseColor + '99';
      }
    });

    evt.detail.chart.update();
  }

  externalTooltipHandler(context) {
    const { chart, tooltip } = context;
    const tooltipEl = chart.canvas.nextElementSibling;

    // Custom event detail that is being passed through
    let detail = {
      detail: {
        el: this.el,
        tooltip,
        chart
      }
    };

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      const mouseoutChart = new CustomEvent("mouseout-chart", detail);

      this.el.dispatchEvent(mouseoutChart);
      return;
    }

    // Set Text
    if (tooltip.dataPoints) {
      let dataPoints = tooltip.dataPoints || [];
      const tooltipFragment = document.createDocumentFragment();

      dataPoints.forEach(item => {
        let titleEl = document.createElement('span');
        let titleText = document.createTextNode(item.label);
        let valueEl = document.createElement('span');
        let valueText = document.createTextNode(item.formattedValue);

        titleEl.classList.add('ChartTooltip-title');
        valueEl.classList.add('ChartTooltip-value');

        titleEl.appendChild(titleText);
        valueEl.appendChild(valueText);

        tooltipFragment.appendChild(titleEl);
        tooltipFragment.appendChild(valueEl);
      });

      while(tooltipEl.firstChild) {
        tooltipEl.firstChild.remove();
      }

      tooltipEl.appendChild(tooltipFragment);
    }

    const { offsetLeft, offsetTop } = chart.canvas;
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = offsetLeft + (tooltip.caretX + 10) + 'px';
    tooltipEl.style.top = offsetTop + (tooltip.caretY + 10) + 'px';


    // Emit event when we are hovering over the segment
    const mouseoverChart = new CustomEvent("mouseover-chart", detail);
    this.el.dispatchEvent(mouseoverChart);
  }

  /**
   * Retrieves data-* attributes from the Chart's container and create instance variables
   * of each in the correct format for Chart.js
   *
   * These are the data-* attributes that are being used:
   * - data-segments = the segments from the block's segments repeater field
   * - data-title = the chart's title
   * - data-type = the type of circle chart that is being used
   *
   * isntance variables that are being created:
   * - this.dataValues
   * - this.colors
   * - this.title
   * - this.type
   * @return null
   */
  setData() {
    let data = JSON.parse(this.el.getAttribute('data-bars'));

    this.dataColors = data.map(item => item.color + 'e0');
    this.dataLabels = data.map(item => item.label);
    this.dataValues = data.map(item => parseInt(item.value, 10));

    this.data = {
      labels: this.dataLabels,
      datasets: [{
        label: this.el.getAttribute('data-title'),
        data: this.dataValues,
        backgroundColor: this.dataColors,
        borderColor: this.dataColors,
        barPercentage: .5,
        maxBarThickness: 38,
        categoryPercentage: 0.9
      }]
    };
  }

  setChartConfig() {
    const userDefinedOpts = JSON.parse(this.el.getAttribute('data-chart-opts'));

    // Move horizontal bar position to accomdate for Label
    // source: https://www.youtube.com/watch?v=rW_-WXuhxp8&t=623s
    const barPosition = {
      id: 'barPosition',
      beforeDatasetsDraw: (chart, args, pluginOptions) => {
        const {data, chartArea } = chart;
        const { top, height } = chartArea;
        const barsCount = data.datasets[0].data.length;
        const barHeight = height / barsCount;

        chart.getDatasetMeta(0).data.forEach((barElement, index) => {
          const magicNumber = 0.7;
          barElement.y = top + (barHeight * (index + magicNumber));
        });
      }
    };

    let mql = window.matchMedia("(min-width: 700px)");

    mql.addEventListener('change', () => {
      // Returning the same values that onResize would for chart.js
      let width  = window.innerWidth;
      let tooltip = (width > 700) ? this.externalTooltipHandler.bind(this) : false;

      this.chart.options.plugins.tooltip.external = tooltip;
      this.chart.update();
    });

    let datalabelsFontSize = (window.innerWidth > 700) ? 22 : 18;
    let tickMarkFontSize = (window.innerWidth > 700) ? 16 : 14;
    let chartFontColor = "#404040"; // $c-default / fs-color("text", "gray-dark");

    // Set opts for chart.js
    this.chartOpts = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: userDefinedOpts.y_axis_title ? true : false, // displays y axis title via user specified value
            text: userDefinedOpts.y_axis_title, // displays y axis title via user specified value
            font: axis_font_opts(),
            color: chartFontColor
          },
          ticks: {
            display: userDefinedOpts.show_data_series_titles, // displays y axis ticks, e.g. the step counts
            font: {
              size: tickMarkFontSize,
              family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
              weight: 'normal'
            },
            position: 'top',
            mirror: true,
            color: chartFontColor,
            labelOffset: -15
          },
          grid: {
            display: false, // shows or hides the horizontal gridlines on the chart
          },
        },
        x: {
          position: 'top',
          border: {
            display: false
          },
          title: {
            display: userDefinedOpts.x_axis_title ? true : false,
            text: userDefinedOpts.x_axis_title,
            font: axis_font_opts(),
            color: chartFontColor
          },
          ticks: {
            display: userDefinedOpts.show_x_axis, // setting false hides the values on the x axis
            font: {
              size: tickMarkFontSize,
              family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
              weight: 'normal'
            },
            color: chartFontColor
          },
          grid: {
            display: userDefinedOpts.show_gridlines, // shows or hides the horizontal gridlines on the chart
            drawOnChartArea: userDefinedOpts.show_gridlines,
            drawTicks: false // remove the ticks that would point to the number on the x axis
          },
          suggestedMax: () => {
            // The maximum number for the Y axis value. This is done dynamically
            // so we can have space for the data labels to appear of the bar without worrying about collision
            let max = this.dataValues.reduce((accumulator, currentValue) => {
              return Math.max(accumulator, currentValue)
            });

            return max * 1.15; // return 110% of the maximum value
          },
          grace: '2%'
        },
        // Displays a line at the bottom of the chart to mimic the design
        x1: {
          position: 'bottom',
          grid: {
            display: true, // shows or hides the horizontal gridlines on the chart
            drawOnChartArea: false,
            drawTicks: false // remove the ticks that would point to the number on the x axis
          },
          ticks: {
            display: false
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false,
          position: 'cursor',
          // We are binding the method to have access to this.toolTipEl
          external: mql.matches ? this.externalTooltipHandler.bind(this) : false,
        }
      },
    };

    // Add Chart.js plugins here
    this.plugins = [barPosition];

    // Add data labels if user wants it to appear by checking the show_data_series_count parameter
    if (userDefinedOpts.show_data_series_counts) {
      this.plugins.push(ChartDataLabels);
      this.chartOpts.plugins.datalabels = {
        font: {
          family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
          size: datalabelsFontSize,
          lineHeight: 1.3,
          weight: 'bolder',
        },
        color: '#480751', // fs-color("text", "purple")
        anchor: 'end',
        clamp: true, // only put the data label inside of the chart
        align: 'start',
        offset: -50
      }
    }
  }
}

const init = () => {
  const HorizontalBarCharts = document.querySelectorAll('[data-horizontal-bar-chart]');

  if (HorizontalBarCharts) {
    HorizontalBarCharts.forEach(chart => {
      new HorizontalBarChart({el: chart});
    })
  }
};

// Init on initial page load
init();
